import {
  AlphanumericLinkList,
  AlphanumericLinkListProps,
} from "design-system/components/blocks/alphanumeric-column-list/alphanumeric-link-list";
import { CtaLinkProps } from "design-system/components/primitives/cta-link/cta-link";
import { createContentfulComponent } from "frontend/contentful/lib/create-contentful-component";
import { AlphanumericLinkListEntry } from "frontend/contentful/schema/blocks";
import { useSearch } from "frontend/hooks/use-search";
import { ModernSearchHit } from "frontend/types";
import { createComponentHeaderProps } from "../primitives/component-header.props";
import { createCtaLinkProps } from "../primitives/cta-link.props";
import { createUseSearchProps } from "../utils/use-search.props";

interface AlphanumericLinkListOptions {
  headerAbove?: boolean;
}

/**
 * Querying ElasticSearch records with provided query.
 */

const QueryContentfulAlphanumericLinkList = ({
  entry,
  options,
}: {
  entry: AlphanumericLinkListEntry;
  options?: AlphanumericLinkListOptions;
}) => {
  const searchProps = createUseSearchProps(entry.fields.linksQuery);
  const { data } = useSearch<ModernSearchHit>(searchProps);
  const headerAbove = options?.headerAbove || false;

  const links: Array<CtaLinkProps> = [];

  data?.hits.forEach((item) => {
    const props: CtaLinkProps = {
      children: item.title,
      href: item.url,
    };
    if (item.title && item.url) links.push(props);
  });

  return (
    <AlphanumericLinkList
      headerContent={createComponentHeaderProps(entry.fields.header)}
      items={links}
      HeadingLevel={headerAbove ? (entry.fields.header ? "h4" : "h3") : "h2"}
      align={entry.fields.alignment}
    />
  );
};

/**
 * Manual selection that overrides the query.
 */
const ManualContentfulAlphanumericLinkList = createContentfulComponent<
  AlphanumericLinkListEntry,
  AlphanumericLinkListOptions
>(({ entry, options, createEditAttributes }) => {
  const headerAbove = options?.headerAbove || false;
  const props: AlphanumericLinkListProps = {
    headerContent: createComponentHeaderProps(entry.fields.header),
    items: [],
    HeadingLevel: headerAbove ? (entry.fields.header ? "h4" : "h3") : "h2",
    editAttributes: {
      block: createEditAttributes({ entry }),
    },
    align: entry.fields?.alignment,
  };

  entry.fields.links?.forEach((item) => {
    const itemProps = createCtaLinkProps(item);
    if (itemProps) props.items?.push(itemProps);
  });

  return <AlphanumericLinkList {...props} />;
});

export const ContentfulAlphanumericLinkList = createContentfulComponent<
  AlphanumericLinkListEntry,
  AlphanumericLinkListOptions
>(({ entry, options }) => {
  if (entry.fields.links && entry.fields.links.length > 0) {
    return (
      <ManualContentfulAlphanumericLinkList entry={entry} options={options} />
    );
  }
  if (entry.fields.linksQuery) {
    return (
      <QueryContentfulAlphanumericLinkList entry={entry} options={options} />
    );
  }
  return null;
});
